import React, { useMemo, useState } from "react";
import '../../App.css';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Avatar,
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    Chip,
    Grid,
    Stack,
    Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CollectionsIcon from "@mui/icons-material/Collections";
import EditIcon from "@mui/icons-material/Edit";

import type { ClientAccess } from "../../models/UserProfile";
interface CollectionSwitcherProps {
    collections: ClientAccess[];
    activeClientId: number | null;
    onSelectClient: (clientId: number) => void;
    onEditClient?: (clientId: number) => void;
    title?: string;
    initiallyExpanded?: boolean;
}

export default function CollectionSwitcher({
    collections,
    activeClientId,
    onSelectClient,
    onEditClient,
    title = "Collections",
    initiallyExpanded = false,
}: CollectionSwitcherProps) {
    const [expanded, setExpanded] = useState(initiallyExpanded);

    const activeCollection = useMemo(
        () => collections.find(c => c.clientId === activeClientId) ?? collections[0],
        [collections, activeClientId]
    );

    if (!collections.length) {
        return null;
    }

    return (
        <Accordion
            expanded={expanded}
            onChange={(_, isExpanded) => setExpanded(isExpanded)}
            sx={{
                width: "100%",
                mb: 3,
                borderRadius: 2,
                overflow: "hidden",
                "&:before": { display: "none" },
            }}
        >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    sx={{ width: "100%" }}
                >
                    <Avatar
                        src={activeCollection?.defaultImageUrl ?? undefined}
                        variant="rounded"
                        sx={{ width: 56, height: 56 }}
                    >
                        <CollectionsIcon />
                    </Avatar>

                    <Box sx={{ flex: 1 }}>
                        <Typography variant="overline" color="text.secondary">
                            {title}
                        </Typography>

                        <Typography variant="h6">
                            {activeCollection?.clientName}
                        </Typography>

                        {activeCollection.tenantName && (
                            <Typography variant="caption" color="text.secondary">
                                In Subscription: {activeCollection.tenantName}
                            </Typography>
                        )}

                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            <Chip
                                size="small"
                                label={`${activeCollection?.artworkCount ?? 0} artworks`}
                            />
                            <Chip
                                size="small"
                                label={activeCollection?.roleName ?? "Role"}
                                color={activeCollection?.roleCode === "Admin" ? "primary" : "default"}
                            />
                        </Stack>

                        {activeCollection?.isDefaultName && (activeCollection.roleCode === "Admin" || activeCollection.roleCode === "Owner") && (
                            <Typography variant="caption" color="warning.main">
                                Tip: Rename default collection name to make it easier to identify.
                            </Typography>
                        )}
                    </Box>
                </Stack>
            </AccordionSummary>

            <AccordionDetails>
                <Grid container spacing={2}>
                    {collections.map(collection => (
                        <Grid size={{ xs:12, sm:6, md:4, lg:3 }} key={collection.clientId}>
                            <CollectionCard
                                collection={collection}
                                selected={collection.clientId === activeClientId}
                                onSelect={() => {
                                    onSelectClient(collection.clientId);
                                    setExpanded(false);
                                }}
                                onEdit={
                                    onEditClient &&
                                        (collection.roleCode === "Admin" || collection.roleCode === "Owner")
                                        ? () => onEditClient(collection.clientId)
                                        : undefined
                                }
                            />
                        </Grid>
                    ))}
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
}

interface CollectionCardProps {
    collection: ClientAccess;
    selected: boolean;
    onSelect: () => void;
    onEdit?: () => void;
}

function CollectionCard({
    collection,
    selected,
    onSelect,
    onEdit,
}: CollectionCardProps) {
    return (
        <Card
            variant={selected ? "elevation" : "outlined"}
            sx={{
                height: "100%",
                borderWidth: selected ? 2 : 1,
                borderColor: selected ? "primary.main" : "divider",
            }}
        >
            <CardActionArea onClick={onSelect} sx={{ height: "100%" }}>
                <div className="collection-image-frame">
                    {collection.defaultImageUrl ? (
                        <img
                            src={collection.defaultImageUrl}
                            alt="Collection"
                            className="catalogue-image"
                            onLoad={(e) => e.currentTarget.classList.add("loaded")}
                        />
                    ) : (
                        <CollectionsIcon color="disabled" sx={{ fontSize: 44 }} />
                    )
                    }
                </div>
                
                <CardContent>
                    <Stack spacing={1}>
                        <Typography variant="subtitle1" noWrap>
                            {collection.clientName}
                        </Typography>

                        {collection.tenantName && (
                            <Typography variant="caption" color="text.secondary">
                                In Subscription: {collection.tenantName}
                            </Typography>
                        )}

                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            <Chip size="small" label={`${collection.artworkCount} artworks`} />
                            <Chip size="small" label={collection.roleName} />
                        </Stack>

                        {onEdit && collection.isDefaultName && (
                            <Typography variant="caption" color="warning.main">
                                Default name
                            </Typography>
                        )}

                        {onEdit && (
                            <Button
                                size="small"
                                startIcon={<EditIcon />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onEdit();
                                }}
                                sx={{ alignSelf: "flex-start" }}
                            >
                                Edit
                            </Button>
                        )}
                    </Stack>
                </CardContent>
            </CardActionArea>
        </Card>
    );
}